"use client";

import { cn } from "@/lib/utils";
import { useRef, useState, useCallback, useEffect } from "react";

import MagnifyingGlassIcon from "@heroicons/react/24/solid/MagnifyingGlassIcon";
import ChevronDownIcon from "@heroicons/react/24/outline/ChevronDownIcon";
import XMarkIcon from "@heroicons/react/24/outline/XMarkIcon";
import ChevronUpIcon from "@heroicons/react/24/outline/ChevronUpIcon";

import { useDebounce } from "use-debounce";
import { SearchOptionDataProps } from "@/components/views/modals/search/types";
import apiFetch from "@/lib/fetch";
import { useRouter } from "next/navigation";
import SearchBlogDropdown from "@/components/views/blog/details/search-input/SearchBlogDropdown";

interface SearchInputTypes {
  description?: string;
  query: string;
  onChangeQuery: (value: string) => void;
  className?: string;
  index: number;
  selectedData?: SearchOptionDataProps;
  setSelectedData?: (data: SearchOptionDataProps) => void;
}

export default function BlogSearchInput(props: SearchInputTypes) {
  const { query, onChangeQuery, setSelectedData, selectedData } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const [loading, setLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [dataCount, setDataCount] = useState(0);
  const [debouncedValue] = useDebounce(query || "", 1000);

  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsFocused(false);
      }
    };

    if (isFocused) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFocused]);

  const handleParentClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (!isFocused) {
        inputRef.current?.focus();
      } else {
        inputRef.current?.blur();
        setIsFocused(false);
      }
    },
    [isFocused],
  );

  const handleInputFocus = (event: React.FocusEvent) => {
    event.preventDefault();
    event.stopPropagation();

    setIsFocused(true);
  };

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleCloseInput = () => {
    setIsFocused(false);
  };

  const mappedData = (data) => {
    return data.map((data) => ({
      ...data,
      onClick: () => {
        router.push(`/blog/${data.slug}`);
      },
    }));
  };

  const searchFetch = useCallback(async () => {
    setLoading(true);
    const response = await apiFetch({ route: `blog/search?q=${query}` });
    setData(mappedData(response.data));
    setDataCount(response.summary?.aggregate?.count || 0);
    setHasMore(response.summary?.aggregate?.count >= 24);
    setLoading(false);
  }, [debouncedValue]);

  const getAllBlogs = useCallback(async () => {
    setLoading(true);
    const response = await apiFetch({ route: `blog/all?index=0` });
    setData(mappedData(response.data));
    setDataCount(response.summary?.aggregate?.count || 0);
    setHasMore(response.summary?.aggregate?.count >= 24);
    setLoading(false);
  }, [debouncedValue]);

  useEffect(() => {
    if (debouncedValue.length > 0 && isFocused) {
      setCurrentPage(0);
      searchFetch();
    }
  }, [debouncedValue, searchFetch]);

  useEffect(() => {
    if (debouncedValue.length == 0 && isFocused) {
      setCurrentPage(1);
      getAllBlogs();
    }
  }, [isFocused, debouncedValue]);

  const getDropdownItems = () => data;

  const nextPage = async (page) => {
    setHasMore(true);

    if (debouncedValue.length) {
      const response = await apiFetch({
        route: `blog/search?q=${query}&index=${page}`,
      });
      if (response?.data.length == 0) {
        setLoading(false);
        setHasMore(false);
      }

      setData((prevState) => {
        return [...prevState, ...mappedData(response.data)];
      });

      return;
    }

    const response = await apiFetch({ route: `blog/all?index=${page}` });
    if (response?.data.length == 0) {
      setLoading(false);
      setHasMore(false);
    }

    setData((prevState) => {
      return [...prevState, ...mappedData(response.data)];
    });
  };

  return (
    <>
      {isFocused && (
        <div className="max-md:flex hidden z-[99999999998] fixed top-0 left-0 bg-white w-full h-full">
          <div className="fixed top-[21px] container flex justify-end">
            <div>
              <XMarkIcon
                onClick={() => handleCloseInput()}
                className="size-8 text-primary cursor-pointer"
              />
            </div>
          </div>
        </div>
      )}

      <div
        id={`search-blog-input-container`}
        role="search"
        ref={containerRef}
        className={cn(
          "relative flex flex-col",
          "w-full md:w-[333.5px]",
          props?.className,
          isFocused &&
            "duration-150 max-md:fixed max-md:container max-md:z-[99999999999] max-md:-translate-y-12",
        )}
      >
        <div
          onClick={(event) => handleParentClick(event)}
          className={cn(
            "py-2 cursor-pointer rounded-full duration-200 border px-4 gap-2 flex justify-between items-center",
            isFocused ? "h-16" : "h-16",
            isFocused
              ? "border-secondary md:shadow-lg"
              : "border-assistant-secondary",
          )}
        >
          <div
            className={cn(
              "aspect-square !h-9 flex duration-200 justify-center items-center rounded-full text-2xl",
              isFocused
                ? "text-white bg-[#15BBAE]"
                : "bg-transparent text-primary",
            )}
          >
            <MagnifyingGlassIcon className="size-6" />
          </div>

          <div className="flex flex-col px-2 w-full duration-200 py-[4.5px]">
            <label
              className={cn(
                "font-bold duration-200",
                isFocused || query
                  ? "text-assistant text-sm h-[18px]"
                  : "text-primary text-base h-[21px]",
              )}
            >
              Blog Ara
            </label>

            <input
              id={`search-blog-input`}
              value={query || ""}
              type="text"
              ref={inputRef}
              className={cn(
                "text-primary duration-200 w-full outline-none font-bold text-base",
                isFocused || query
                  ? "h-[21px] opacity-100"
                  : "h-[0px] opacity-0",
              )}
              onChange={(e) => {
                onChangeQuery(e.target.value);

                if (setSelectedData && e.target.value.length) {
                  setSelectedData({
                    id: 0,
                    title: "",
                    slug: "",
                  });
                }
              }}
              onClick={(event) => handleInputClick(event)}
              onFocus={(event) => handleInputFocus(event)}
            />
          </div>

          <div className="p-2.5 rounded-full border border-assistant-secondary">
            {!isFocused && (
              <ChevronDownIcon
                className={cn(
                  "size-5",
                  isFocused ? "text-secondary " : "text-primary",
                )}
              />
            )}

            {isFocused && (
              <ChevronUpIcon
                className={cn(
                  "size-5",
                  isFocused ? "text-secondary " : "text-[#15BBAE]",
                )}
              />
            )}
          </div>
        </div>

        {isFocused && (
          <SearchBlogDropdown
            hasMore={hasMore}
            index={props.index}
            large={props.className}
            loading={loading}
            itemCount={dataCount}
            data={getDropdownItems()}
            show={isFocused}
            next={nextPage}
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
          />
        )}
      </div>
    </>
  );
}
